import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { TFunction } from 'i18next';
import { GenderOptions } from 'pages/Customers/CustomerManagementModals/constants';
import { GroupPrice } from 'types/MerchantTypes';
import { formatDate } from 'utils/common/Time';

export const exportCustomers = async (
  t: TFunction,
  data: any[],
  StoresTextMap: Record<string, string>,
  groupPrice: GroupPrice[]
) => {
  const now = new Date();
  const formattedDate = `${now.getFullYear()}${String(
    now.getMonth() + 1
  ).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`;

  const genderText = (t: TFunction, gender: number | string) => {
    const value = String(gender ?? '0');
    const option = GenderOptions(t).find((o) => o.value === value);
    return option ? option.label : '';
  };

  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('Customers');

  // Column headers mapped with t('')
  worksheet.columns = [
    { header: t('Account Number'), key: 'account_number', width: 18 },
    { header: t('Name'), key: 'name', width: 28 },
    { header: t('Contact Name'), key: 'contact_name', width: 22 }, // you can leave blank if not used
    { header: t('Register Branch'), key: 'register_branch', width: 26 },
    { header: t('Email'), key: 'email', width: 28 },
    { header: t('Phone Number'), key: 'phone_number', width: 18 },
    { header: t('Address'), key: 'address', width: 36 },
    { header: t('District'), key: 'district', width: 18 },
    { header: t('Province'), key: 'province', width: 18 },
    { header: t('Postal Code'), key: 'zip', width: 14 },
    { header: t('Register Date'), key: 'register_date', width: 16 },
    { header: t('Comments'), key: 'comments', width: 24 },
    { header: t('Birth Date'), key: 'birth_date', width: 16 },
    { header: t('Gender'), key: 'gender', width: 14 },
    { header: t('Card ID / Tax ID'), key: 'tax_id', width: 24 },
    { header: t('Product Price Group'), key: 'group_price_name', width: 22 },
    { header: t('Taxes'), key: 'taxable', width: 10 },
    { header: t('Facebook'), key: 'facebook', width: 20 },
    { header: t('Line'), key: 'line', width: 20 },
    { header: t('Instagram'), key: 'instagram', width: 20 },
    { header: t('Shipping Address'), key: 'shipping_address', width: 36 },
    { header: t('Date Limit'), key: 'date_limit', width: 16 },
    {
      header: t('Receivable Limit'),
      key: 'receivable_limit',
      width: 18,
      style: { numFmt: '#,##0.00' },
    },
  ];

  // Freeze header row
  worksheet.views = [{ state: 'frozen', ySplit: 1 }];

  data.forEach((c) => {
    const row = {
      account_number: c.account_number ?? '',
      name: c.first_name,
      contact_name: c.last_name,
      register_branch:
        StoresTextMap[c.register_store_id] || c.register_store_id,

      email: c.email ?? '',
      phone_number: String(c.phone_number) ?? '',
      address: c.address1,
      district: '',
      province: '',
      zip: c.zip ?? '',
      register_date: formatDate(c.register_date ?? ''),
      comments: c.comments ?? '',
      birth_date: formatDate(c.birth_date ?? ''),
      gender: genderText(t, Number(c.gender)),
      tax_id: c.card_id ?? '',
      group_price_name:
        groupPrice?.[Number(c.group_price)]?.name ??
        t('Group Price') + String(Number(c.group_price) + 1 || ''),
      taxable: Number(c.taxable ?? 0),
      facebook: c.facebook ?? '',
      line: c.line ?? '',
      instagram: c.instagram ?? '',
      shipping_address: c.shipping_address ?? '',
      date_limit: Number(c.date_limit ?? 0),
      receivable_limit: Number(c.receivable_limit ?? 0),
    };

    const added = worksheet.addRow(row);

    // Force Excel to treat account number as text (preserve leading zeros)
    const accountCell = added.getCell('account_number');
    if (typeof accountCell.value === 'string') {
      accountCell.numFmt = '@';
    }
    const phoneNumberCell = added.getCell('phone_number');
    if (typeof accountCell.value === 'string') {
      phoneNumberCell.numFmt = '@';
    }
  });

  // Header styling
  const header = worksheet.getRow(1);
  header.font = { bold: true };
  header.alignment = { vertical: 'middle' };
  header.height = 20;

  const buffer = await workbook.xlsx.writeBuffer();
  saveAs(new Blob([buffer]), `export_customers_${formattedDate}.xlsx`);
};
